import { useEffect, useState, useCallback, useRef } from "react"
import styled from "styled-components"
import { toast } from "react-hot-toast"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import {
  X,
  Eye,
  Pencil,
  Paperclip,
  Save,
  Trash2,
  Download as DownloadIcon,
  ImageIcon,
} from "lucide-react"
import { confirmDialog } from "../../utils/confirmDialog"

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.2);
  z-index: 950;
`

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 560px;
  max-width: 95vw;
  background: white;
  z-index: 960;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  animation: slidein 0.18s ease-out;

  @keyframes slidein {
    from { transform: translateX(20px); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }

  @media (max-width: 900px) {
    width: 100vw;
    max-width: 100vw;
  }
`

const Header = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`

const Title = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  small {
    font-size: 11px;
    color: #94a3b8;
  }
`

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid ${(p) => (p.active ? "#2563eb" : "#e5e7eb")};
  background: ${(p) => (p.active ? "#eff6ff" : "white")};
  color: ${(p) => (p.active ? "#1d4ed8" : "#475569")};
  border-radius: 6px;
  cursor: pointer;

  &:hover { background: ${(p) => (p.active ? "#dbeafe" : "#f8fafc")}; }
`

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  &:hover { background: #f1f5f9; color: #0f172a; }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const EditorWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: ${(p) => (p.dragging ? "2px dashed #2563eb" : "none")};
  background: ${(p) => (p.dragging ? "#eff6ff" : "transparent")};
  transition: background 0.1s, border 0.1s;
`

const Textarea = styled.textarea`
  flex: 1;
  min-height: 0;
  padding: 14px 18px;
  border: none;
  outline: none;
  resize: none;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #1f2937;
  background: transparent;
`

const Preview = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 22px 32px;
  font-size: 14px;
  line-height: 1.7;
  color: #1f2937;

  h1, h2, h3, h4 { color: #0f172a; margin: 18px 0 8px; }
  h1 { font-size: 22px; }
  h2 { font-size: 18px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  h3 { font-size: 16px; }
  p { margin: 8px 0; }
  ul, ol { padding-left: 22px; }
  li { margin: 3px 0; }
  code { background: #f1f5f9; padding: 1px 5px; border-radius: 3px; font-size: 12.5px; }
  pre { background: #0f172a; color: #f1f5f9; padding: 12px; border-radius: 6px; overflow-x: auto; font-size: 12.5px; }
  pre code { background: transparent; padding: 0; color: inherit; }
  blockquote { border-left: 3px solid #cbd5e1; margin: 10px 0; padding: 4px 14px; color: #475569; background: #f8fafc; }
  table { border-collapse: collapse; margin: 10px 0; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 10px; font-size: 13px; }
  th { background: #f8fafc; }
  img { max-width: 100%; border-radius: 6px; }
  a { color: #2563eb; }
`

const AttachmentsSection = styled.div`
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 10px 18px;
  flex-shrink: 0;
  max-height: 35%;
  overflow-y: auto;
`

const AttachmentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const AttachmentsTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const UploadBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  background: #2563eb;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
  input { display: none; }
`

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12.5px;
  background: white;
  border: 1px solid #e5e7eb;

  & + & { margin-top: 4px; }
  &:hover { background: #f1f5f9; }
`

const FileMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  strong {
    font-size: 12.5px;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    font-size: 10.5px;
    color: #94a3b8;
  }
`

const FileAction = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #e5e7eb; color: #0f172a; }
`

const Empty = styled.div`
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  padding: 10px 0;
`

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export default function TaskNotePanel({ isOpen, onClose, chartId, task }) {
  const [mode, setMode] = useState("edit") // 'edit' | 'preview'
  const [content, setContent] = useState("")
  const [attachments, setAttachments] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [dragging, setDragging] = useState(false)
  const textareaRef = useRef(null)

  // 노트/첨부 불러오기
  const load = useCallback(async () => {
    if (!chartId || !task?.id) return
    setLoading(true)
    try {
      const r = await fetch(
        `/request/task-note?chart_id=${chartId}&task_id=${encodeURIComponent(task.id)}`,
        { credentials: "include" }
      )
      const data = await r.json()
      if (data.success) {
        setContent(data.content_md || "")
        setAttachments(data.attachments || [])
        setDirty(false)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [chartId, task?.id])

  useEffect(() => {
    if (isOpen) {
      setMode("edit")
      load()
    }
  }, [isOpen, load])

  // 저장
  const save = async () => {
    if (!chartId || !task?.id) return
    setSaving(true)
    try {
      const r = await fetch("/request/task-note", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ chart_id: chartId, task_id: task.id, content_md: content }),
      })
      const data = await r.json()
      if (!data.success) throw new Error(data.message)
      setDirty(false)
      toast.success("저장됨")
    } catch (e) {
      toast.error(e.message || "저장 실패")
    } finally {
      setSaving(false)
    }
  }

  // Ctrl+S 저장
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault()
        save()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
    // eslint-disable-next-line
  }, [isOpen, content])

  // 파일 업로드 + 이미지면 본문에 마크다운 자동 삽입
  const uploadFile = useCallback(async (file) => {
    if (!chartId || !task?.id || !file) return null
    const fd = new FormData()
    fd.append("chart_id", String(chartId))
    fd.append("task_id", String(task.id))
    fd.append("file", file)
    try {
      const r = await fetch("/request/task-attachment", {
        method: "POST",
        credentials: "include",
        body: fd,
      })
      const data = await r.json()
      if (!data.success) throw new Error(data.message)
      setAttachments((prev) => [data.attachment, ...prev])
      // 이미지면 마크다운 본문에 자동 삽입
      if ((data.attachment.mime_type || "").startsWith("image/")) {
        const md = `\n![${data.attachment.original_name}](/request/task-attachment/${data.attachment.id}?inline=1)\n`
        setContent((prev) => prev + md)
        setDirty(true)
      }
      return data.attachment
    } catch (e) {
      toast.error(e.message || "업로드 실패")
      return null
    }
  }, [chartId, task?.id])

  // 드래그앤드롭
  const onDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    const files = Array.from(e.dataTransfer.files || [])
    files.forEach(uploadFile)
  }

  // 클립보드 붙여넣기 (이미지 캡처)
  const onPaste = (e) => {
    const items = e.clipboardData?.items || []
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile()
        if (f) uploadFile(f)
      }
    }
  }

  const removeAttachment = async (att) => {
    if (!(await confirmDialog(`"${att.original_name}" 첨부파일을 삭제할까요?`))) return
    try {
      const r = await fetch(`/request/task-attachment/${att.id}`, {
        method: "DELETE",
        credentials: "include",
      })
      const data = await r.json()
      if (!data.success) throw new Error(data.message)
      setAttachments((prev) => prev.filter((a) => a.id !== att.id))
    } catch (e) {
      toast.error(e.message || "삭제 실패")
    }
  }

  if (!isOpen) return null

  return (
    <>
      <Overlay onClick={onClose} />
      <Panel>
        <Header>
          <Title>
            <h2>{task?.name || "작업 노트"}</h2>
            <small>참고 자료 · 설계 · 설명 (Ctrl+S 저장 / 이미지 붙여넣기·드래그)</small>
          </Title>
          <HeaderActions>
            <IconButton active={mode === "edit"} onClick={() => setMode("edit")}>
              <Pencil size={12} /> 편집
            </IconButton>
            <IconButton active={mode === "preview"} onClick={() => setMode("preview")}>
              <Eye size={12} /> 미리보기
            </IconButton>
            <IconButton onClick={save} disabled={saving} style={{ borderColor: dirty ? "#f59e0b" : undefined, color: dirty ? "#b45309" : undefined }}>
              <Save size={12} /> {saving ? "저장 중…" : (dirty ? "저장 *" : "저장")}
            </IconButton>
            <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
          </HeaderActions>
        </Header>

        <Body>
          <EditorWrap
            dragging={dragging}
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            {mode === "edit" ? (
              <Textarea
                ref={textareaRef}
                value={content}
                placeholder="이 작업에 대한 설명·설계·참고자료를 자유롭게 작성하세요.&#10;&#10;마크다운 지원:&#10;• **굵게**  *기울임*  ~~취소~~&#10;• # 제목, - 목록, > 인용, `코드`&#10;• 표, 체크리스트 ([ ] / [x])&#10;• 이미지: 그냥 붙여넣기 또는 드래그&#10;• 다른 파일: 아래 [+ 파일 업로드] 사용"
                onChange={(e) => { setContent(e.target.value); setDirty(true) }}
                onPaste={onPaste}
              />
            ) : (
              <Preview>
                {content.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                ) : (
                  <Empty>아직 작성된 노트가 없습니다.</Empty>
                )}
              </Preview>
            )}
          </EditorWrap>

          <AttachmentsSection>
            <AttachmentsHeader>
              <AttachmentsTitle>
                <Paperclip size={14} />
                첨부파일 ({attachments.length})
              </AttachmentsTitle>
              <UploadBtn>
                <Paperclip size={12} /> + 파일 업로드
                <input
                  type="file"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    files.forEach(uploadFile)
                    e.target.value = ""
                  }}
                  multiple
                />
              </UploadBtn>
            </AttachmentsHeader>

            {attachments.length === 0 ? (
              <Empty>아직 첨부된 파일이 없습니다. 위 버튼이나 본문에 파일을 드래그하세요.</Empty>
            ) : (
              attachments.map((att) => {
                const isImg = (att.mime_type || "").startsWith("image/")
                return (
                  <AttachmentItem key={att.id}>
                    {isImg ? <ImageIcon size={14} color="#0ea5e9" /> : <Paperclip size={14} color="#64748b" />}
                    <FileMeta>
                      <strong>{att.original_name}</strong>
                      <span>{formatSize(att.file_size)} · {att.mime_type || "unknown"}</span>
                    </FileMeta>
                    <FileAction
                      onClick={() => window.open(`/request/task-attachment/${att.id}`, "_blank")}
                      title="다운로드"
                    >
                      <DownloadIcon size={14} />
                    </FileAction>
                    <FileAction
                      onClick={() => removeAttachment(att)}
                      title="삭제"
                      style={{ color: "#dc2626" }}
                    >
                      <Trash2 size={14} />
                    </FileAction>
                  </AttachmentItem>
                )
              })
            )}
          </AttachmentsSection>
        </Body>
      </Panel>
    </>
  )
}
